import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from 'utils/analytics';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';

import { OnboardingCheckerItem } from '@/modules/Onboarding/components/SetupGuide/OnboardingCheckerItem';
import { useCreatePeriodicOperationUtils } from '@/modules/periodicTasks/components/Create/logic';
import AddEditWarehouse from '@/modules/settings/property/components/Warehouses/AddEditWarehouse';
import { useCreateInboundDeliveryUtils } from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/logic';
import { Collapse, Typography } from '@/shared/components/atoms';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const InventoryCollapse = () => {
  const { openModal } = useModal();
  const { locale } = useRouter();
  const { onboardingChecks, user } = useAuth();
  const { t } = useTranslation('onboarding');
  const [selectedAsset] = useStore((store) => store?.asset);
  const { openCreatePeriodicOperationModal } =
    useCreatePeriodicOperationUtils();
  const { openCreateInboundDeliveryModal } = useCreateInboundDeliveryUtils();

  const handleAddWarehouse = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_add_warehouse', user);
    openModal(<AddEditWarehouse property={selectedAsset} />, {
      variant: 'full',
      title: (
        <div>
          <Typography variant='h2' weight='bold'>
            {t('add-warehouse', { ns: 'common' })}
          </Typography>
        </div>
      ),
    });
  }, [openModal, t, user, selectedAsset]);

  const handleAddInventoryItems = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_add_inventory_items', user);
    openCreateInboundDeliveryModal();
  }, [openCreateInboundDeliveryModal, user]);

  const handleCreatePeriodicOperation = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_create_periodic_operation', user);
    openCreatePeriodicOperationModal();
  }, [openCreatePeriodicOperationModal, user]);

  return (
    <Collapse
      defaultValue
      bgColor='bg-background-neutral-99'
      childrenClassName='flex flex-col gap-16'
      header={<div className='font-semi-bold'>{t('inventory')}</div>}
    >
      <OnboardingCheckerItem
        action={handleAddWarehouse}
        description={t('add-your-warehouse-description')}
        status={onboardingChecks?.hasWarehouse ? 'done' : 'idle'}
        stepNumber={1}
        title={t('add-your-warehouse')}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_ADD_WAREHOUSE,
          })
        }
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/crear-un-almacen'
            : locale === 'fr'
            ? 'https://aydi.helpjuice.com/fr_FR/crear-un-almacen'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/crear-un-almacen'
            : 'https://aydi.helpjuice.com/en_US/configurar-tu-cuenta/crear-un-almacen'
        }
      />
      <OnboardingCheckerItem
        action={handleAddInventoryItems}
        description={t('add-inventory-items-description')}
        status={onboardingChecks?.hasInventoryItem ? 'done' : 'idle'}
        stepNumber={2}
        title={t('add-inventory-items')}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_VIEW_ADD_NEW_STOCK,
          })
        }
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/agregar-productos-de-inventario-al-almacen'
            : locale === 'fr'
            ? 'https://aydi.helpjuice.com/fr_FR/agregar-productos-de-inventario-al-almacen'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/agregar-productos-de-inventario-al-almacen'
            : 'https://aydi.helpjuice.com/en_US/configurar-tu-cuenta/agregar-productos-de-inventario-al-almacen'
        }
      />
      <OnboardingCheckerItem
        action={handleCreatePeriodicOperation}
        description={t('create-periodic-operation-description')}
        status={onboardingChecks?.hasPeriodicOperation ? 'done' : 'idle'}
        stepNumber={3}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_ADD_PERIODIC_OPERATIONS,
          })
        }
        title={t('create-periodic-operation')}
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/modulo-de-inventario/create-periodic-operations-from-aydi-eye'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/%D8%A5%D8%AF%D8%A7%D8%B1%D8%A9-%D8%A7%D9%84%D9%85%D8%AE%D8%A7%D8%B2%D9%86/create-periodic-operations-from-aydi-eye'
            : 'https://aydi.helpjuice.com/en_US/inventory-module/create-periodic-operations-from-aydi-eye'
        }
      />
    </Collapse>
  );
};

export { InventoryCollapse };
